import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { create } from "zustand";
import { getXsrfToken } from "./csrf";

// Required by laravel-echo's reverb broadcaster
window.Pusher = Pusher;

/**
 * Creates a new Echo instance connected to Laravel Reverb.
 * Returns null if VITE_REVERB_APP_KEY is not configured (e.g., in tests/CI).
 */
const createEchoInstance = (): Echo<"reverb"> | null => {
  const key = import.meta.env.VITE_REVERB_APP_KEY;
  if (!key) return null;

  const scheme = import.meta.env.VITE_REVERB_SCHEME ?? "https";
  const forceTLS = scheme === "https";

  const port = Number(import.meta.env.VITE_REVERB_PORT);

  const authEndpoint = `${import.meta.env.VITE_BACKEND_URL}/broadcasting/auth`;

  return new Echo<"reverb">({
    broadcaster: "reverb",
    key,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wsPort: port || 80,
    wssPort: port || 443,
    forceTLS,
    enabledTransports: ["ws", "wss"],
    channelAuthorization: {
      transport: "ajax" as const,
      endpoint: authEndpoint,
      customHandler: (params, callback) => {
        fetch(authEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-Requested-With": "XMLHttpRequest",
            "X-XSRF-TOKEN": getXsrfToken() ?? "",
          },
          credentials: "include",
          body: new URLSearchParams({
            socket_id: params.socketId,
            channel_name: params.channelName,
          }),
        })
          .then((response) => {
            if (!response.ok)
              throw new Error(`Auth failed: ${response.status}`);
            return response.json();
          })
          .then((data) => callback(null, data))
          .catch((error) => callback(error, null));
      },
    },
  });
};

interface EchoStore {
  echo: Echo<"reverb"> | null;
}

export const useEchoStore = create<EchoStore>(() => ({
  echo: null,
}));

/**
 * Returns the Echo instance, creating it lazily on first access.
 * Returns null when Reverb is not configured (tests/CI).
 */
export const getEcho = (): Echo<"reverb"> | null => {
  const existing = useEchoStore.getState().echo;
  if (existing) return existing;

  const instance = createEchoInstance();
  if (instance) {
    useEchoStore.setState({ echo: instance });
  }
  return instance;
};

export const resetEcho = () => {
  const current = useEchoStore.getState().echo;
  current?.disconnect();
  const instance = createEchoInstance();
  useEchoStore.setState({ echo: instance });
};
